import { z } from 'zod'

export const useChangePasswordValidator = () => {
  const changePasswordSchema = z.object({
    email: z.string().email('Email inválido'),
    expiredPassword: z.string().min(1, 'Senha atual é obrigatória'),
    newPassword: z
      .string()
      .min(8, 'A senha deve ter no mínimo 8 caracteres')
      .regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula')
      .regex(/[0-9]/, 'A senha deve conter pelo menos um número')
      .regex(
        /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/,
        'A senha deve conter pelo menos um caractere especial',
      ),
  })

  type ChangePasswordForm = z.infer<typeof changePasswordSchema>
  type ChangePasswordErrors = Partial<Record<keyof ChangePasswordForm, string>>

  const createForm = (): ChangePasswordForm => ({
    email: '',
    expiredPassword: '',
    newPassword: '',
  })

  const createEmptyErrors = (): ChangePasswordErrors => ({
    email: '',
    expiredPassword: '',
    newPassword: '',
  })

  const validateForm = (
    form: ChangePasswordForm,
  ): { isValid: boolean; errors: ChangePasswordErrors } => {
    const errors = createEmptyErrors()

    try {
      changePasswordSchema.parse(form)
      return { isValid: true, errors }
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((e) => {
          const field = e.path[0] as keyof ChangePasswordForm
          errors[field] = e.message
        })
      }
      return { isValid: false, errors }
    }
  }

  const resetForm = (form: ChangePasswordForm): void => {
    form.email = ''
    form.expiredPassword = ''
    form.newPassword = ''
  }

  const resetErrors = (errors: ChangePasswordErrors, field?: keyof ChangePasswordErrors): void => {
    if (field) {
      errors[field] = ''
    } else {
      Object.keys(errors).forEach((key) => {
        errors[key as keyof ChangePasswordErrors] = ''
      })
    }
  }

  const validatePassword = (password: string) => ({
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password),
    hasMinLength: password.length >= 8,
  })

  return {
    changePasswordSchema,
    createForm,
    createEmptyErrors,
    validateForm,
    resetForm,
    resetErrors,
    validatePassword,
  }
}

export type ChangePasswordForm = z.infer<
  ReturnType<typeof useChangePasswordValidator>['changePasswordSchema']
>
export type ChangePasswordErrors = Partial<Record<keyof ChangePasswordForm, string>>
